/**
 * zinspectorDataProvider: Atomic CRM's data provider on top of the zInspector public API.
 *
 * | CRM resource  | zInspector source                                          | Writable |
 * |---------------|------------------------------------------------------------|----------|
 * | companies     | properties in an account group (`CRM – <Market>`, `Test`), | no       |
 * |               | with their zData details, leases and open tasks            |          |
 * | contacts      | contacts linked to an account property                     | no       |
 * | deals         | Sales tasks (list + per-task detail for the deal fields)   | yes      |
 * | sales         | zInspector users                                           | no       |
 * | leases        | leases (engagements), with their tenants                   | no       |
 * | tickets       | Support tasks                                              | no       |
 * | activities    | Follow-up tasks: calls, meetings, comments (activities.ts) | yes      |
 * | leads         | Prospect tasks (leads.ts, leadConfig.ts)                   | yes      |
 *
 * zInspector has no server-side equivalent of the screens' PostgREST-style filters, so each
 * resource is read whole (cached for a minute) and filtered, sorted and paginated in the
 * browser with the same FakeRest engine the demo uses. Only tasks are writable with an API key;
 * every other write is refused with a pointer to the zInspector dashboard.
 */
import {
  HttpError,
  type CreateParams,
  type GetListParams,
  type GetManyReferenceParams,
  type Identifier,
  type RaRecord,
  type UpdateParams,
} from "ra-core";
import fakeRestDataProvider from "ra-data-fakerest";

import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { defaultConfiguration } from "../../root/defaultConfiguration";
import { withSupabaseFilterAdapter } from "../fakerest/internal/supabaseAdapter";
import type { CrmDataProvider } from "../types";
import {
  getSession,
  isFakeZinspector,
  ziFetch,
  ziFetchAll,
  type QueryParams,
  type ZiUser,
} from "./client";
import {
  ACTIVATION_TARGET_DAYS,
  DEAL_TYPES,
  LAST_TOUCH_LOOKBACK_DAYS,
  ONBOARDING_PROCESS_PK,
  parseAccountGroup,
  STAGES,
  TASK_TYPE_PK,
  TASK_TYPES,
} from "./crmConfig";
import {
  contactToCrm,
  dealToTaskPatch,
  isAccountProperty,
  isBoardStatus,
  leaseToEngagement,
  taskToDeal,
  taskToTicket,
  userToSale,
  type ZcrmDeal,
  type ZiContact,
  type ZiLease,
  type ZiProperty,
  type ZiTask,
  type ZiZDataRow,
} from "./mappers";
import { buildAccounts } from "./accounts";
import {
  accountWriteError,
  accountWriteSupport,
  buildAccountWrites,
} from "./accountWrites";
import {
  type MeetingOutcome,
  activityToTaskPatch,
  buildActivityWrites,
  isActivityTask,
  taskToActivity,
  type ActivityInput,
  type ZcrmActivity,
} from "./activities";
import {
  buildCommercialTermsRequest,
  type CommercialTerms,
} from "./commercialTerms";
import { buildNewClientRequests, type NewClientInput } from "./newClient";
import { createLeadMethods } from "./leadsProvider";
import { publish, publishDealMove } from "./events";
import { registerPipelineListeners } from "./pipelineListeners";
import type { LeadInput, ZcrmLead } from "./leads";

/** zCRM's own methods on top of Atomic CRM's data provider. */
export type ZcrmDataProvider = CrmDataProvider & {
  /** The account markets: user groups named "CRM – <Market>" (or "Test"), by full group name. */
  getMarkets: () => Promise<{ value: string; label: string }[]>;
  /** Starts onboarding for a won deal: an Onboarding task on the account (writable live). */
  startOnboarding: (input: {
    companyId: Identifier;
    accountName: string;
    effectiveDate: string;
    ownerId?: Identifier | null;
  }) => Promise<{ id: number }>;
  /** Saves an account's commercial terms (zData "Commercial" area). Demo (fake zInspector) only. */
  saveCommercialTerms: (
    companyId: Identifier,
    values: Partial<CommercialTerms>,
    previous: Partial<CommercialTerms>,
  ) => Promise<void>;
  /** Creates an account with its details and main contact. Demo (fake zInspector) only. */
  createClient: (input: NewClientInput) => Promise<{ id: number }>;
  /** Turns a lead into a Sales deal at "New lead" and marks the lead qualified. */
  convertLead: (
    lead: ZcrmLead,
    deal: Partial<ZcrmDeal>,
  ) => Promise<{ dealId: Identifier }>;
  /** Books a meeting for a lead on the host's calendar (booking.ts). */
  bookMeeting: ReturnType<typeof createLeadMethods>["bookMeeting"];
  /** A lead that is really a support request becomes a Support task (Help desk). */
  moveLeadToHelpDesk: (id: Identifier) => Promise<void>;
};

const CACHE_TTL_MS = 60_000;
const DAY_MS = 24 * 60 * 60 * 1000;
/** zData is read one account at a time (no list route), so it is kept longer and read in parallel. */
const ZDATA_CACHE_TTL_MS = 5 * 60_000;
const ZDATA_CONCURRENCY = 8;

/**
 * zCRM settings come from crmConfig.ts (there is no settings table). Served by getConfiguration
 * so the app reloads them after sign-in and on every page, whatever is in browser storage.
 */
export const zcrmConfiguration: ConfigurationContextValue = {
  ...defaultConfiguration,
  dealStages: STAGES.map(({ id, label }) => ({ value: id, label })),
  dealCategories: DEAL_TYPES.map(({ id, label }) => ({ value: id, label })),
  dealPipelineStatuses: ["won"],
};
const DETAIL_CONCURRENCY = 6;
/** The task statuses of deals off the board: Won, Lost, Snoozed. */
const END_STATUSES = new Set<string>(
  STAGES.filter((s) => "end" in s && s.end).map((s) => s.status),
);
const DEFAULT_PRIORITY = 3;
/** Everyone's activities include those closed in this many days (show rate, Today). */
const RECENT_ACTIVITY_DAYS = 30;

/** Atomic CRM resources zInspector has no source for yet: they read as empty. */
const EMPTY_RESOURCES = new Set([
  "tasks",
  "contact_notes",
  "deal_notes",
  "tags",
  "activity_log",
]);

type AnyRecord = RaRecord & Record<string, unknown>;

const readOnlyError = (resource: string) =>
  new HttpError(
    `${resource} are read-only in zCRM: make this change in the zInspector dashboard.`,
    405,
  );

const unsupported = (feature: string) => () =>
  Promise.reject(
    new Error(`${feature} is managed in zInspector, not in zCRM.`),
  );

const baseResource = (resource: string) => resource.replace(/_summary$/, "");

const runWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<R>,
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let next = 0;
  const lanes = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (next < items.length) {
        const index = next++;
        results[index] = await worker(items[index]);
      }
    },
  );
  await Promise.all(lanes);
  return results;
};

/** "Contains" filters (`field_q`) are regular expressions in FakeRest: match what was typed, literally. */
const escapeTextFilters = (filter: Record<string, unknown> = {}) =>
  Object.fromEntries(
    Object.entries(filter).map(([key, value]) => [
      key,
      key.endsWith("_q") && typeof value === "string"
        ? value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        : value,
    ]),
  );

/** Filters, sorts and paginates records with the demo's FakeRest engine (PostgREST filter syntax). */
const queryRecords = (
  resource: string,
  records: AnyRecord[],
  params: GetListParams,
) =>
  withSupabaseFilterAdapter(
    fakeRestDataProvider({ [resource]: records.map((r) => ({ ...r })) }, false),
  ).getList(resource, { ...params, filter: escapeTextFilters(params.filter) });

const toNumberOrUndefined = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return value != null && value !== "" && Number.isFinite(parsed)
    ? parsed
    : undefined;
};

/** `/api/zdata/` has been seen as a list of `{Property, zData[]}`; accept the other plausible shapes too. */
const flattenZData = (response: unknown): ZiZDataRow[] => {
  const items = Array.isArray(response)
    ? response
    : response && typeof response === "object" && "results" in response
      ? (response as { results: unknown[] }).results
      : [response];
  return items.flatMap((item) => {
    if (item && typeof item === "object" && "zData" in item) {
      return (item as { zData: ZiZDataRow[] }).zData ?? [];
    }
    return item && typeof item === "object" && "area_name" in item
      ? [item as ZiZDataRow]
      : [];
  });
};

export const createZinspectorDataProvider = (): ZcrmDataProvider => {
  const cache = new Map<string, { at: number; value: Promise<unknown> }>();
  const taskDetails = new Map<string, Promise<ZiTask>>();

  const cached = <T>(
    key: string,
    load: () => Promise<T>,
    ttl = CACHE_TTL_MS,
  ): Promise<T> => {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < ttl) return hit.value as Promise<T>;
    const value = load();
    cache.set(key, { at: Date.now(), value });
    value.catch(() => cache.delete(key));
    return value;
  };

  const invalidate = (prefix: string) => {
    for (const key of cache.keys()) {
      if (key.startsWith(prefix)) cache.delete(key);
    }
  };

  /** The deal fields only come with the task detail; keyed by LastModified so an edit refetches. */
  const getTaskDetail = (task: ZiTask): Promise<ZiTask> => {
    const key = `${task.id}:${task.LastModified ?? ""}`;
    let detail = taskDetails.get(key);
    if (!detail) {
      detail = ziFetch<ZiTask>(`/tasks/${task.id}/`);
      taskDetails.set(key, detail);
      detail.catch(() => taskDetails.delete(key));
    }
    return detail;
  };

  /** The properties that are accounts (in a "CRM – <Market>" or "Test" user group). */
  const loadAccountProperties = () =>
    cached("account-properties", async () =>
      (await ziFetchAll<ZiProperty>("/propertiesCursor/")).filter(
        isAccountProperty,
      ),
    );

  const loadRawContacts = () =>
    cached("contacts-raw", () =>
      ziFetchAll<ZiContact>("/contactsCursor/", { include_properties: true }),
    );

  /** zData has no list route across properties, so each account's "Account" area is read once and cached longer. */
  const loadAccountZData = (properties: ZiProperty[]) =>
    cached(
      `zdata:${properties.map((p) => p.id).join(",")}`,
      async () => {
        const rows = await runWithConcurrency(
          properties,
          ZDATA_CONCURRENCY,
          async (property) => {
            try {
              return flattenZData(
                await ziFetch("/zdata/", {
                  params: { property_id: property.id },
                }),
              );
            } catch {
              return []; // Details are a bonus: the account still lists without them.
            }
          },
        );
        return new Map<Identifier, ZiZDataRow[]>(
          properties.map((property, i) => [property.id, rows[i]]),
        );
      },
      ZDATA_CACHE_TTL_MS,
    );

  const loadAccounts = () =>
    cached("companies", async () => {
      const since = new Date(Date.now() - LAST_TOUCH_LOOKBACK_DAYS * DAY_MS)
        .toISOString()
        .slice(0, 10);
      const [properties, leases, openTasks, recentTasks, contacts] =
        await Promise.all([
          loadAccountProperties(),
          ziFetchAll<ZiLease>("/leasesCursor/", { include_tenants: true }),
          ziFetchAll<ZiTask>("/tasks/", { active: true }),
          // Closed tasks count as touches too: a logged call, a won deal, a closed ticket.
          ziFetchAll<ZiTask>("/tasks/", { updated_after: since }),
          loadRawContacts(),
        ]);
      const zData = await loadAccountZData(properties);
      const contactNames = new Map<Identifier, string[]>();
      for (const contact of contacts) {
        const name =
          `${contact.FirstName ?? ""} ${contact.LastName ?? ""}`.trim();
        for (const link of contact.Properties ?? []) {
          contactNames.set(link.id, [
            ...(contactNames.get(link.id) ?? []),
            name,
          ]);
        }
      }
      return buildAccounts({
        properties,
        leases,
        openTasks,
        recentTasks,
        zData,
        contactNames,
      });
    });

  const loadContacts = () =>
    cached("contacts", async () => {
      const [contacts, properties] = await Promise.all([
        loadRawContacts(),
        loadAccountProperties(),
      ]);
      const accountIds = new Set<Identifier>(properties.map((p) => p.id));
      return contacts
        .filter((c) => (c.Properties ?? []).some((p) => accountIds.has(p.id)))
        .map((c) => contactToCrm(c, accountIds));
    });

  /** Tickets are Support tasks; an account page shows its own, closed ones included. */
  const loadTickets = (companyId?: number) =>
    cached(`tickets:${companyId ?? "active"}`, async () => {
      const params: QueryParams = { Type: TASK_TYPES.ticket };
      if (companyId) params.Property = companyId;
      else params.active = true;
      return (await ziFetchAll<ZiTask>("/tasks/", params)).map(taskToTicket);
    });

  /**
   * Calls, meetings and comments, with their notes (task detail): an account's, done ones
   * included; or everyone's open ones plus those closed recently (Today, booking, show rate).
   */
  const loadActivities = (companyId?: number) =>
    cached(`activities:${companyId ?? "active"}`, async () => {
      const type = { Type: TASK_TYPES.meeting };
      const lists = companyId
        ? [ziFetchAll<ZiTask>("/tasks/", { ...type, Property: companyId })]
        : [
            ziFetchAll<ZiTask>("/tasks/", { ...type, active: true }),
            ziFetchAll<ZiTask>("/tasks/", {
              ...type,
              updated_after: new Date(
                Date.now() - RECENT_ACTIVITY_DAYS * DAY_MS,
              )
                .toISOString()
                .slice(0, 10),
            }),
          ];
      const tasks = [
        ...new Map(
          (await Promise.all(lists)).flat().map((t) => [t.id, t]),
        ).values(),
      ].filter(isActivityTask);
      const details = await runWithConcurrency(
        tasks,
        DETAIL_CONCURRENCY,
        getTaskDetail,
      );
      return details.map(taskToActivity);
    });

  const createActivity = async (
    input: ActivityInput,
  ): Promise<ZcrmActivity> => {
    const { create, edit } = buildActivityWrites(input, getSession()?.user.id);
    const created = await ziFetch<{ id?: number; pk?: number }>(
      "/task/create/",
      { method: "POST", body: create },
    );
    const id = created?.id ?? created?.pk;
    if (!id) throw new Error("zInspector logged the call but returned no id.");
    await ziFetch(`/task/edit/${id}/`, { method: "PATCH", body: edit });
    invalidate("activities:");
    invalidate("companies");
    return taskToActivity(await ziFetch<ZiTask>(`/tasks/${id}/`));
  };

  const updateActivity = async (
    id: Identifier,
    data: Partial<ZcrmActivity> & { outcome?: MeetingOutcome },
  ): Promise<ZcrmActivity> => {
    const patch = activityToTaskPatch(data);
    if (Object.keys(patch).length > 0) {
      await ziFetch(`/task/edit/${id}/`, { method: "PATCH", body: patch });
      invalidate("activities:");
      invalidate("companies");
    }
    const activity = taskToActivity(await ziFetch<ZiTask>(`/tasks/${id}/`));
    if (data.outcome === "held" || data.outcome === "no_show") {
      publish(data.outcome === "held" ? "meeting.held" : "meeting.no_show", {
        meetingId: id,
        companyId: activity.company_id,
      });
    } else if (data.starts_at) {
      publish("meeting.rescheduled", {
        meetingId: id,
        startsAt: data.starts_at,
      });
    }
    return activity;
  };

  /**
   * The board reads open deals only (`active=true`, as in the spec); an account page asks for
   * one property's deals, closed ones included.
   */
  /**
   * Deals that left the board (won, lost, snoozed) and changed since a date: the board's Won,
   * Lost and Snoozed views. Bounded by date because each deal needs its detail read.
   */
  const loadClosedDeals = (since: string) =>
    cached(`deals:closed:${since}`, async () => {
      const tasks = (
        await ziFetchAll<ZiTask>("/tasks/", {
          Type: TASK_TYPES.deal,
          updated_after: since,
        })
      ).filter((task) => END_STATUSES.has(String(task.Status)));
      const details = await runWithConcurrency(
        tasks,
        DETAIL_CONCURRENCY,
        getTaskDetail,
      );
      return details
        .map(taskToDeal)
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    });

  const loadDeals = (companyId?: number) =>
    cached(`deals:${companyId ?? "active"}`, async () => {
      const params: QueryParams = { Type: TASK_TYPES.deal };
      if (companyId) params.Property = companyId;
      else params.active = true;
      const tasks = (await ziFetchAll<ZiTask>("/tasks/", params)).filter(
        (task) => isBoardStatus(task.Status),
      );
      const details = await runWithConcurrency(
        tasks,
        DETAIL_CONCURRENCY,
        getTaskDetail,
      );
      // zInspector has no manual card order: newest activity first in each column.
      return details
        .map(taskToDeal)
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
        .map((deal, index) => ({ ...deal, index }));
    });

  const loadMarkets = () =>
    cached("markets", async () => {
      const groups = await ziFetchAll<string | { Name?: string }>(
        "/userGroup/",
      );
      return groups
        .map((group) =>
          typeof group === "string" ? group : (group.Name ?? ""),
        )
        .map((name) => ({ name, ...parseAccountGroup(name) }))
        .filter((group) => group.isAccount)
        .map((group) => ({
          value: group.name,
          label: group.market ?? group.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    });

  const createClient = async (input: NewClientInput) => {
    if (!isFakeZinspector()) {
      throw new Error(
        "zInspector's public API cannot create accounts yet: this screen only previews the requests.",
      );
    }
    const [propertyRequest] = buildNewClientRequests(input);
    const property = await ziFetch<{ id: number }>(propertyRequest.path, {
      method: propertyRequest.method,
      body: propertyRequest.body,
    });
    for (const request of buildNewClientRequests(input, property.id).slice(1)) {
      await ziFetch(request.path, {
        method: request.method,
        body: request.body,
      });
    }
    invalidate("companies");
    invalidate("account-properties");
    invalidate("zdata:");
    invalidate("contacts");
    return { id: property.id };
  };

  const saveCommercialTerms = async (
    companyId: Identifier,
    values: Partial<CommercialTerms>,
    previous: Partial<CommercialTerms>,
  ) => {
    if (!isFakeZinspector()) {
      throw new Error(
        "zInspector's public API cannot save zData yet: this screen only previews the request.",
      );
    }
    const request = buildCommercialTermsRequest(companyId, values, previous);
    if (!request) return;
    await ziFetch(request.path, { method: request.method, body: request.body });
    invalidate("companies");
    invalidate("zdata:");
  };

  /** Saves the account fields edited in the accounts table (see accountWrites.ts). */
  const updateAccount = async (
    id: Identifier,
    data: Record<string, unknown>,
    previous: Record<string, unknown>,
  ) => {
    const account = (await loadAccounts()).find(
      (a) => String(a.id) === String(id),
    );
    if (!account) throw new HttpError(`Account ${id} not found`, 404);
    const writes = buildAccountWrites(account, data, previous);
    const blocked = accountWriteError(
      writes,
      accountWriteSupport(isFakeZinspector()),
    );
    if (blocked) throw blocked;
    for (const write of writes) {
      const result = await ziFetch<{ id?: number; pk?: number }>(write.path, {
        method: write.method,
        body: write.body,
      });
      const createdId = result?.id ?? result?.pk;
      if (write.thenEdit && createdId) {
        await ziFetch(`/task/edit/${createdId}/`, {
          method: "PATCH",
          body: write.thenEdit,
        });
      }
    }
    invalidate("companies");
    invalidate("activities:");
    invalidate("account-properties");
    invalidate("zdata:");
    const updated = (await loadAccounts()).find(
      (a) => String(a.id) === String(id),
    );
    return updated ?? { ...account, ...data };
  };

  const startOnboarding = async ({
    companyId,
    accountName,
    effectiveDate,
    ownerId,
  }: {
    companyId: Identifier;
    accountName: string;
    effectiveDate: string;
    ownerId?: Identifier | null;
  }) => {
    const start = new Date(effectiveDate || Date.now());
    const owner = ownerId ? Number(ownerId) : getSession()?.user.id;
    const created = await ziFetch<{ id?: number; pk?: number }>(
      "/task/create/",
      {
        method: "POST",
        body: {
          Action: TASK_TYPE_PK.Onboarding,
          Priority: DEFAULT_PRIORITY,
          Property: Number(companyId),
          Description: `Onboarding — ${accountName}`,
          StartDate: start.toISOString(),
          EndDate: new Date(
            start.getTime() + ACTIVATION_TARGET_DAYS * DAY_MS,
          ).toISOString(),
          ManagedBy: owner,
          AssignedToUser: owner,
          ...(ONBOARDING_PROCESS_PK
            ? { todoListPk: ONBOARDING_PROCESS_PK }
            : {}),
        },
      },
    );
    const id = created?.id ?? created?.pk;
    if (!id)
      throw new Error("zInspector started onboarding but returned no id.");
    invalidate("companies");
    return { id };
  };

  const loadUsers = () =>
    cached("sales", async () =>
      (await ziFetchAll<ZiUser>("/user/")).map(userToSale),
    );

  const loadLeases = (companyId?: number) =>
    cached(`leases:${companyId ?? "active"}`, async () => {
      const params: QueryParams = { include_tenants: true };
      if (companyId) params.Property = companyId;
      else params.active = true;
      return (await ziFetchAll<ZiLease>("/leasesCursor/", params)).map(
        leaseToEngagement,
      );
    });

  const loadRecords = async (
    resource: string,
    filter: Record<string, unknown> = {},
  ): Promise<AnyRecord[]> => {
    const companyId = toNumberOrUndefined(filter.company_id);
    switch (resource) {
      case "companies":
        return (await loadAccounts()) as AnyRecord[];
      case "contacts":
        return (await loadContacts()) as AnyRecord[];
      case "deals":
        return (
          typeof filter.closed_since === "string"
            ? await loadClosedDeals(filter.closed_since)
            : await loadDeals(companyId)
        ) as AnyRecord[];
      case "sales":
        return (await loadUsers()) as AnyRecord[];
      case "leases":
        return (await loadLeases(companyId)) as AnyRecord[];
      case "tickets":
        return (await loadTickets(companyId)) as AnyRecord[];
      case "activities":
        return (await loadActivities(companyId)) as AnyRecord[];
      case "leads":
        return (await leads.loadLeads()) as AnyRecord[];
      default:
        if (EMPTY_RESOURCES.has(resource)) return [];
        throw new HttpError(`Unknown resource "${resource}"`, 404);
    }
  };

  const getList = async (resource: string, params: GetListParams) => {
    const name = baseResource(resource);
    const records = await loadRecords(name, params.filter);
    // `closed_since` picks which deals to load; it isn't a field to filter on.
    const { closed_since: _since, ...filter } = params.filter ?? {};
    return queryRecords(name, records, { ...params, filter });
  };

  const fetchDeal = async (id: Identifier): Promise<ZcrmDeal> =>
    taskToDeal(await ziFetch<ZiTask>(`/tasks/${id}/`));

  const getOne = async (resource: string, { id }: { id: Identifier }) => {
    const name = baseResource(resource);
    if (name === "deals") return { data: await fetchDeal(id) };
    if (name === "leads") return { data: await leads.fetchLead(id) };

    const record = (await loadRecords(name)).find(
      (r) => String(r.id) === String(id),
    );
    if (!record) throw new HttpError(`${name} ${id} not found`, 404);
    return { data: record };
  };

  const patchDeal = async (
    id: Identifier,
    data: Partial<ZcrmDeal>,
    previous: Partial<ZcrmDeal>,
  ): Promise<ZcrmDeal> => {
    const patch = dealToTaskPatch(data, previous);
    if (Object.keys(patch).length === 0) {
      return { ...previous, ...data, id } as ZcrmDeal;
    }
    await ziFetch(`/task/edit/${id}/`, { method: "PATCH", body: patch });
    invalidate("deals:");
    invalidate("companies");
    const deal = await fetchDeal(id);
    if (data.stage && data.stage !== previous.stage) {
      publishDealMove(deal, previous.stage ?? "");
    }
    return { ...deal, index: data.index ?? previous.index ?? 0 };
  };

  const createDeal = async (data: Partial<ZcrmDeal>): Promise<ZcrmDeal> => {
    const me = getSession()?.user.id;
    const owner = data.sales_id ? Number(data.sales_id) : me;
    const created = await ziFetch<{ id?: number; pk?: number }>(
      "/task/create/",
      {
        method: "POST",
        body: {
          Action: TASK_TYPE_PK.Sales,
          Priority: DEFAULT_PRIORITY,
          Property: data.company_id ? Number(data.company_id) : undefined,
          Description: data.name ?? "",
          ManagedBy: owner,
          AssignedToUser: owner,
          AssignedToContact: data.contact_ids?.length
            ? Number(data.contact_ids[0])
            : undefined,
        },
      },
    );
    const id = created?.id ?? created?.pk;
    if (!id) throw new Error("zInspector created the deal but returned no id.");

    // The create route only takes the core task fields; the deal fields go through edit.
    const newStage = STAGES[0].id;
    const {
      name: _name,
      company_id: _company,
      sales_id: _owner,
      contact_ids: _contacts,
      ...rest
    } = data;
    await patchDeal(
      id,
      { ...rest, stage: rest.stage ?? newStage },
      { stage: newStage },
    );
    invalidate("deals:");
    return fetchDeal(id);
  };

  const leads = createLeadMethods({
    cached,
    invalidate,
    getTaskDetail,
    createDeal,
  });

  const provider = {
    getList,
    getOne,

    async getMany(resource: string, { ids }: { ids: Identifier[] }) {
      const name = baseResource(resource);
      if (name === "deals") {
        return { data: await Promise.all(ids.map(fetchDeal)) };
      }
      const wanted = new Set(ids.map(String));
      const records = await loadRecords(name);
      return { data: records.filter((r) => wanted.has(String(r.id))) };
    },

    getManyReference(resource: string, params: GetManyReferenceParams) {
      return getList(resource, {
        ...params,
        filter: { ...params.filter, [params.target]: params.id },
      });
    },

    async update(resource: string, params: UpdateParams) {
      if (baseResource(resource) === "companies") {
        return {
          data: await updateAccount(
            params.id,
            params.data,
            params.previousData,
          ),
        };
      }
      if (baseResource(resource) === "activities") {
        return { data: await updateActivity(params.id, params.data) };
      }
      if (baseResource(resource) === "leads") {
        return {
          data: await leads.updateLead(
            params.id,
            params.data,
            params.previousData,
          ),
        };
      }
      if (baseResource(resource) !== "deals") throw readOnlyError(resource);
      return {
        data: await patchDeal(params.id, params.data, params.previousData),
      };
    },

    async updateMany(
      resource: string,
      { ids, data }: { ids: Identifier[]; data: Partial<ZcrmDeal> },
    ) {
      if (baseResource(resource) !== "deals") throw readOnlyError(resource);
      for (const id of ids) await patchDeal(id, data, {});
      return { data: ids };
    },

    async create(resource: string, params: CreateParams) {
      if (baseResource(resource) === "activities") {
        return { data: await createActivity(params.data as ActivityInput) };
      }
      if (baseResource(resource) === "leads") {
        return { data: await leads.createLead(params.data as LeadInput) };
      }
      if (baseResource(resource) !== "deals") throw readOnlyError(resource);
      return { data: await createDeal(params.data) };
    },

    delete(resource: string) {
      return Promise.reject(readOnlyError(resource));
    },

    deleteMany(resource: string) {
      return Promise.reject(readOnlyError(resource));
    },

    // Atomic CRM's custom methods: zInspector owns users, settings and contact records.
    signUp: unsupported("Sign-up"),
    salesCreate: unsupported("Creating users"),
    salesUpdate: unsupported("Editing users"),
    updatePassword: unsupported("Passwords"),
    mergeContacts: unsupported("Merging contacts"),
    updateConfiguration: unsupported("Settings"),
    unarchiveDeal: async () => undefined,
    isInitialized: async () => true,
    getMarkets: loadMarkets,
    createClient,
    startOnboarding,
    saveCommercialTerms,
    convertLead: leads.convertLead,
    moveLeadToHelpDesk: leads.moveLeadToHelpDesk,
    bookMeeting: leads.bookMeeting,
    getConfiguration: async (): Promise<ConfigurationContextValue> =>
      zcrmConfiguration,
  };

  registerPipelineListeners({
    dealsOf: (companyId) => loadDeals(Number(companyId)),
    moveDeal: (deal, stage) => patchDeal(deal.id, { stage }, deal),
  });

  return provider as unknown as ZcrmDataProvider;
};
